// Compose the Google Chat card for a block trade.
//
// The figures are formatted here rather than on the backend because every one of them is
// already formatted by this screen: lakhs and crores from `metricFormatters`, Indian digit
// grouping from `@/utils/formatters`, and the annual implied returns from `impliedReturns`,
// which are derived client-side so the Orders & Execution screen and this one cannot
// disagree about an analyst's target. A second implementation on the server would drift
// from this one until chat and screen quoted different numbers for the same block.
//
// What goes over the wire is therefore rendered strings in a structure, not a finished
// message: the server arranges them into Chat's two-column card widget and never touches a
// value. A card rather than a text message because Chat's text format has no table —
// aligned columns there need a monospace ``` fence, which renders the whole deal small and
// grey and cannot carry bold. The card aligns its own columns in the space's normal font,
// The server lays the figures out as a four-column grid — label, value, label, value —
// so each label gets about a quarter of the card's width. Labels below are kept short for
// that reason: one that wraps to a second line pushes its own row out of step with the
// value beside it, which is how a figure gets read against the wrong label.
//
// The backend assembles and posts it (`POST /api/block-trades/{id}/send-to-chat/`) and logs
// the send against the operator and the row.

import { formatCurrency, formatDate, formatDateTime, formatQuantityInLakhs } from '@/utils/formatters';
import {
  BlockTrade,
  BlockTradeChatFigure,
  BlockTradeChatSection,
  BlockTradeChatSharePayload,
  BlockTradeMetric,
  BlockTradeSnapshot,
} from '@/types/blockTrade';
import { BlockTradeImpliedReturns } from './impliedReturns';
import {
  asCmp,
  asDays,
  asMoney,
  asPct,
  asPrecisePct,
  asPrice,
  asQty,
  MetricFormatter,
  renderMetric,
} from './metricFormatters';

/** A snapshot figure, flagged when the desk typed the number in. The flag travels as data
 *  rather than as text baked into the value, so the card can badge it in its own style —
 *  and a figure the desk typed must never reach chat looking like a sourced one, which is
 *  how a desk ends up trading on a guess. */
const metricFigure = (
  label: string,
  metric: BlockTradeMetric | undefined,
  format: MetricFormatter,
): BlockTradeChatFigure => ({
  label,
  value: renderMetric(metric, format),
  manual: Boolean(metric?.manual),
});

/** A figure off the row itself, which is never a desk override. */
const valueFigure = (label: string, value: string): BlockTradeChatFigure => ({ label, value });

const impliedReturnFigures = (impliedReturns: BlockTradeImpliedReturns): BlockTradeChatFigure[] => {
  const entries: [string, number | null][] = [
    ['IC', impliedReturns.ic],
    ['Analyst', impliedReturns.analyst],
    ['Model 3yr', impliedReturns.model3yr],
    ['Model 5yr', impliedReturns.model5yr],
  ];

  // Only what the page actually rendered. A null is a figure the desk was not shown, and a
  // dash for it would imply the OMS was asked and had nothing.
  return entries
    .filter((entry): entry is [string, number] => entry[1] != null)
    .map(([label, value]) => valueFigure(label, asPrecisePct(value)));
};

/**
 * @param blockTrade      The row being shared.
 * @param snapshot        Its captured desk snapshot — the figures the decision was taken on.
 * @param impliedReturns  Derived by `deriveImpliedReturns` against the same snapshot.
 */
export const buildBlockTradeChatCard = (
  blockTrade: BlockTrade,
  snapshot: BlockTradeSnapshot,
  impliedReturns: BlockTradeImpliedReturns,
): BlockTradeChatSharePayload => {
  const isOurBuy = blockTrade.our_side === 'BUY';
  const priceLabel = blockTrade.quoted_price_label;
  const strategies = blockTrade.investment_strategy_details.map(strategy => strategy.name);

  // Figures are ordered in reading pairs: the grid fills left to right, so each two
  // consecutive figures below share a row. Reordering this list reorders what the desk
  // reads against what.
  const sections: BlockTradeChatSection[] = [
    {
      header: '1. Block Deal Offer',
      figures: [
        valueFigure('Qty on Offer', formatQuantityInLakhs(blockTrade.quantity, 2)),
        valueFigure(priceLabel, formatCurrency(blockTrade.ask_price, undefined, 0)),
        metricFigure('Block Deal Value', snapshot.block_deal_offer?.block_deal_value, asMoney),
      ],
    },
    {
      header: '2. Our Desk Mandate',
      figures: [
        metricFigure(
          isOurBuy ? 'Our Buy Limit' : 'Our Sell Limit',
          snapshot.desk_mandate?.limit_price,
          asPrice,
        ),
        metricFigure('CMP', snapshot.desk_mandate?.cmp, asCmp),
        metricFigure('Mandate Qty', snapshot.desk_mandate?.mandate_qty, asQty),
        metricFigure('Mandate Value', snapshot.desk_mandate?.mandate_value, asMoney),
        metricFigure('Executed Qty', snapshot.desk_mandate?.executed_qty, asQty),
        metricFigure('Unexecuted Qty', snapshot.desk_mandate?.unexecuted_qty, asQty),
        metricFigure(
          'Disc/Prem vs CMP',
          snapshot.desk_mandate?.block_discount_premium_pct,
          asPrecisePct,
        ),
      ],
    },
    {
      header: isOurBuy ? '3. Portfolio & Entry Cost' : '3. Portfolio & Realized P&L',
      figures: [
        metricFigure('Holding % Now', snapshot.portfolio?.current_holding_pct, asPrecisePct),
        metricFigure('Target %', snapshot.portfolio?.target_position_pct, asPrecisePct),
        metricFigure('Cost Price', snapshot.portfolio?.cost_price, asPrice),
        metricFigure('Investment Cost', snapshot.portfolio?.investment_cost, asMoney),
        metricFigure('Current Value', snapshot.portfolio?.current_value, asMoney),
        metricFigure('Unrealized P&L %', snapshot.portfolio?.unrealized_pnl_pct, asPct),
        isOurBuy
          ? metricFigure(
            'Avg Cost Post-Block',
            snapshot.portfolio?.new_combined_avg_cost_post_block,
            asPrice,
          )
          : metricFigure(
            'Realized P&L',
            snapshot.portfolio?.realized_pnl_on_block,
            asMoney,
          ),
        metricFigure(
          'Holding % Post-Block',
          snapshot.portfolio?.combined_holding_pct_post_block,
          asPrecisePct,
        ),
        metricFigure(
          isOurBuy ? 'Remaining Buy Qty' : 'Remaining Qty',
          snapshot.portfolio?.post_trade_remaining_qty,
          asQty,
        ),
        metricFigure('Holding Qty', snapshot.portfolio?.holding_qty, asQty),
        metricFigure('Total AUM', snapshot.portfolio?.total_aum, asMoney),
      ],
    },
    {
      header: '4. Liquidity',
      figures: [
        metricFigure('21-Day ADV', snapshot.liquidity?.adv_21d, asQty),
        metricFigure('21-Day Avg Value', snapshot.liquidity?.avg_value_21d, asMoney),
        metricFigure('21-Day Avg Price', snapshot.liquidity?.avg_price_21d, asPrice),
        metricFigure(
          'Days of ADV',
          snapshot.liquidity?.days_of_adv_handled_by_block,
          asDays,
        ),
      ],
    },
  ];

  const impliedReturn = impliedReturnFigures(impliedReturns);
  if (impliedReturn.length > 0) {
    sections.push({ header: '5. Implied Annual Return', figures: impliedReturn });
  }

  return {
    title: `BLOCK TRADE — WE ${blockTrade.our_side} ${blockTrade.company_symbol}`,
    subtitle: `${blockTrade.company_name} • ${formatDate(blockTrade.trade_date)} • ${blockTrade.status_display}`,
    byline: `${blockTrade.broker_name} (holding a ${blockTrade.party_type_display}) • ${strategies.join(', ')}`,
    sections,
    footer:
      `Captured ${formatDateTime(snapshot.captured_at)} — snapshot, not live. `
      + 'Quantities in lakhs, values in crores.',
  };
};
